/**
 * GlobeLoadingPlaceholder — lightweight SVG wireframe globe animation
 * used as a loading fallback while the Three.js SatelliteGlobe loads.
 */

const TABOO_RED = '#ab0013';
const TABOO_RED_LIGHT = '#d4001a';

/* ── Placeholder data dots (stagger-revealed via CSS delay) ──────── */

const DATA_POINTS = [
  { lat: 25, lng: -10, delay: 0.2 },
  { lat: 35, lng: 20, delay: 0.4 },
  { lat: 50, lng: -5, delay: 0.6 },
  { lat: 15, lng: 45, delay: 0.3 },
  { lat: -5, lng: 10, delay: 0.5 },
  { lat: 40, lng: -30, delay: 0.7 },
  { lat: 10, lng: 30, delay: 0.8 },
  { lat: 30, lng: 50, delay: 0.35 },
  { lat: 55, lng: 15, delay: 0.9 },
] as const;

/* ── Wireframe globe paths (meridians + parallels) ───────────────── */

const CX = 300;
const CY = 300;
const R = 220;

function generateGlobeLines(): string[] {
  const paths: string[] = [];

  for (let i = 0; i < 8; i++) {
    const angle = (i / 8) * Math.PI;
    const rx = R * Math.cos(angle);
    const ry = R;
    if (Math.abs(rx) > 2) {
      const dir = rx > 0 ? 1 : 0;
      paths.push(
        `M ${CX},${CY - ry} A ${Math.abs(rx).toFixed(1)},${ry} 0 0 ${dir} ${CX},${CY + ry} A ${Math.abs(rx).toFixed(1)},${ry} 0 0 ${dir === 1 ? 0 : 1} ${CX},${CY - ry}`
      );
    }
  }

  for (let i = 1; i < 6; i++) {
    const y = CY + R * Math.cos((i / 6) * Math.PI);
    const rx = R * Math.sin((i / 6) * Math.PI);
    const ry = (rx * 0.15).toFixed(1);
    paths.push(
      `M ${(CX - rx).toFixed(1)},${y.toFixed(1)} A ${rx.toFixed(1)},${ry} 0 0 1 ${(CX + rx).toFixed(1)},${y.toFixed(1)} A ${rx.toFixed(1)},${ry} 0 0 1 ${(CX - rx).toFixed(1)},${y.toFixed(1)}`
    );
  }

  return paths;
}

const GLOBE_LINES = generateGlobeLines();

const KEYFRAMES = `
@keyframes glp-fadeStroke {
  to { opacity: 0.35; }
}
@keyframes glp-spinSlow {
  to { transform: rotate(360deg); }
}
@keyframes glp-dotReveal {
  to { opacity: 1; }
}
@keyframes glp-pulse {
  0%, 100% { r: var(--glp-r-min); opacity: 0.6; }
  50%      { r: var(--glp-r-max); opacity: 0; }
}
`;

interface GlobeLoadingPlaceholderProps {
  height?: string;
  minHeight?: string;
}

export function GlobeLoadingPlaceholder({
  height = '600px',
  minHeight = '400px',
}: GlobeLoadingPlaceholderProps) {
  return (
    <div
      style={{
        width: '100%',
        height,
        minHeight,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
      }}
    >
      <style>{KEYFRAMES}</style>

      <svg
        viewBox="0 0 600 600"
        style={{
          width: 'min(420px, 65vw)',
          height: 'min(420px, 65vw)',
        }}
        aria-label="Loading globe..."
        role="img"
      >
        <defs>
          <radialGradient id="glp-grad" cx="50%" cy="45%" r="50%">
            <stop offset="0%" stopColor="rgba(171,0,19,0.08)" />
            <stop offset="100%" stopColor="transparent" />
          </radialGradient>
          <filter id="glp-glow">
            <feGaussianBlur stdDeviation="3" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        <circle
          cx={CX}
          cy={CY}
          r={R}
          fill="url(#glp-grad)"
          stroke={TABOO_RED}
          strokeWidth="1"
          opacity="0.3"
        >
          <animate attributeName="opacity" values="0.2;0.4;0.2" dur="3s" repeatCount="indefinite" />
        </circle>

        {GLOBE_LINES.map((d, i) => (
          <path
            key={i}
            d={d}
            fill="none"
            stroke={TABOO_RED}
            strokeWidth="0.6"
            opacity="0"
            style={{ animation: `glp-fadeStroke 0.6s ease-out ${(0.1 * i).toFixed(1)}s forwards` }}
          />
        ))}

        <circle
          cx={CX}
          cy={CY}
          r={R}
          fill="none"
          stroke={TABOO_RED_LIGHT}
          strokeWidth="1.5"
          opacity="0.5"
        />

        <circle
          cx={CX}
          cy={CY}
          r={R + 15}
          fill="none"
          stroke={TABOO_RED}
          strokeWidth="1"
          strokeDasharray="8 20"
          opacity="0.4"
          style={{
            animation: 'glp-spinSlow 20s linear infinite',
            transformOrigin: `${CX}px ${CY}px`,
          }}
        />

        {DATA_POINTS.map((dot, i) => {
          const x = CX + (dot.lng / 60) * R * 0.8;
          const y = CY - (dot.lat / 60) * R * 0.8;
          const rMin = 4;
          const rMax = 14;

          return (
            <g
              key={i}
              opacity="0"
              style={{ animation: `glp-dotReveal 0.5s ease ${dot.delay}s forwards` }}
            >
              <circle
                cx={x}
                cy={y}
                r={rMin}
                fill="none"
                stroke={TABOO_RED_LIGHT}
                strokeWidth="1"
                opacity="0.5"
              >
                <animate
                  attributeName="r"
                  values={`${rMin};${rMax};${rMin}`}
                  dur="2.5s"
                  repeatCount="indefinite"
                  begin={`${dot.delay}s`}
                />
                <animate
                  attributeName="opacity"
                  values="0.6;0;0.6"
                  dur="2.5s"
                  repeatCount="indefinite"
                  begin={`${dot.delay}s`}
                />
              </circle>

              <circle
                cx={x}
                cy={y}
                r={5}
                fill={TABOO_RED_LIGHT}
                opacity="0.85"
                filter="url(#glp-glow)"
              />
              <circle cx={x} cy={y} r={2.5} fill="#fff" opacity="0.9" />
            </g>
          );
        })}

        <ellipse
          cx={CX}
          cy={CY}
          rx={R}
          ry={R * 0.15}
          fill="none"
          stroke={TABOO_RED_LIGHT}
          strokeWidth="1"
          opacity="0.25"
        />
      </svg>
    </div>
  );
}
